import {Engine} from "platform/engine/Engine";
import {XhrUtil} from "core/util/XhrUtil";
import {OptInLessonPayload, SetLessons} from "core/redux/lessons/LessonsReduxActions";
import {HttpReject} from "platform/network/http/Http";
import Utils from "platform/util/Utils";
import {LanguageUtil} from "platform/util/LanguageUtil";
import {LangCode} from "platform/enum/LangCode";
import {AccountState} from "core/state/AccountState";
import Platform from "platform/Platform";
import {ServiceType} from "enum/ServiceType";
import {TSMap} from "typescript-map";
import {
    GetUIConfigurationResponse,
    Lesson,
    Tutorial,
    TutorialCategory
} from "protocol/config/GetUIConfigurationResponse";
import {GetUIConfigurationRequest} from "protocol/config/GetUIConfigurationRequest";
import {SetUrls} from "platform/redux/core/CoreActions";
import {UrlType} from "platform/enum/UrlType";
import {SetAccountSettingsConfig} from "core/redux/settings/SettingsReduxActions";
import {ActivityType} from "enum/ActivityType";

export default class LessonsEngine extends Engine {

    private static _instance: LessonsEngine;

    public static instance(): LessonsEngine {
        return this._instance || (this._instance = new this());
    }

    public doOptInLesson = async (payload: OptInLessonPayload) => {
        const {Id} = payload.lesson;
        this._logger.debug("Opt in lesson: " + Id);
        const request: any = {activityType: 106};
        const answer: [HttpReject, any] = await Utils.to(this.sendToLessonsService(request, "RegisterActivity"));
        if (answer[0]) {
            this._logger.debug("Failed opt in lesson");
        }
    }

    public doFetchUIConfiguration = async () => {
        this._logger.debug("Fetch lessons");
        const langCode: LangCode = LanguageUtil.languageCode();
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const request: GetUIConfigurationRequest = {languageCode: langCode, userId: accountState.userId};
        const answer: [HttpReject, GetUIConfigurationResponse] = await Utils.to(this.sendToWebProfitService(request, "GetUIConfiguration"));
        if (answer[0]) {
            this._logger.debug("Failed fetch lessons");
        } else {
            const categories: TutorialCategory[] = [];
            const lessons: TSMap<number, Lesson[]> = new TSMap<number, Lesson[]>();
            const {IsLearningCenterEnabled, Tutorials, EbookUrl, AutochartistUrl, EconomicCalendarUrl, AnnualStatementYears} = answer[1] || {};
            if (IsLearningCenterEnabled && Tutorials && Tutorials.TutorialsDictionary) {
                Object.keys(Tutorials.TutorialsDictionary).forEach((key: string) => {
                    const tutorials: Tutorial[] = Tutorials.TutorialsDictionary[key];
                    if (Utils.isArrayNotEmpty(tutorials)) {
                        tutorials.forEach((tutorial: Tutorial) => {
                            if (tutorial.Allowed && tutorial.Tutorial) {
                                const category: TutorialCategory = tutorial.Tutorial.TutorialCategory;
                                if (category && Utils.isNotNull(category.TutorialCategoryId)) {
                                    let groupLessons: Lesson[] = lessons.get(category.TutorialCategoryId);
                                    if (!groupLessons) {
                                        categories.push(category);
                                        lessons.set(category.TutorialCategoryId, groupLessons = []);
                                    }
                                    groupLessons.push(tutorial.Tutorial);
                                }
                            }
                        });
                    }
                });
                categories.sort((c1: TutorialCategory, c2: TutorialCategory) => Utils.compareNumber(c1.TutorialCategoryId, c2.TutorialCategoryId));
                lessons.forEach((ls: Lesson[]) => {
                    ls.sort((l1: Lesson, l2: Lesson) => Utils.compareNumber(l1.DisplayOrder, l2.DisplayOrder));
                });
            }
            Platform.dispatch(SetLessons({categories, lessons, EbookUrl}));
            Platform.dispatch(SetUrls({urls: [
                    {type: UrlType.AutochartistUrl, url: AutochartistUrl},
                    {type: UrlType.EconomicCalendarUrl, url: EconomicCalendarUrl}
                ]}));
            Platform.dispatch(SetAccountSettingsConfig({
                AnnualStatementYears: AnnualStatementYears || []
            }));
        }
    }

    public doRequestEnableEbook = async () => {
        await XhrUtil.sendAuthenticated({ActivityType: ActivityType.EBookNotAllowed}, "WebProfitSite/Activities/RegisterActivity");
    }

    private sendToLessonsService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticated(request, "LearningCenter/Tutorials/" + path);
    }

    private sendToWebProfitService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticated(request, "WebProfitServer/WebProfitClientService.svc/json/" + path);
    }
}
